import React, { forwardRef } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { isSlotNode, nodeMetaChanges, formatCondition } from './agentDiff';
import MetaChangesTable from './MetaChangesTable';
import NodeContentDiff from './NodeContentDiff';
import SlotVariantDiff from './SlotVariantDiff';
import { renderDiffText } from './renderDiffText';

const preSx = { fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontFamily: 'inherit' };

// Full read-only render of a slot node's variants + fallback -- used when the
// whole node was added or removed, so there's nothing to diff, just to show.
function SlotFull({ data }) {
  return (
    <Box>
      {(data.variants || []).map((v, i) => (
        <Box key={i} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
            if {formatCondition(v.condition)}
          </Typography>
          <Typography component="pre" sx={preSx}>{renderDiffText(v.content || '')}</Typography>
        </Box>
      ))}
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>fallback</Typography>
      <Typography component="pre" sx={preSx}>{renderDiffText(data.fallback || '')}</Typography>
    </Box>
  );
}

function renderNodeBody(node) {
  return isSlotNode(node) ? <SlotFull data={node.data} /> : (
    <Typography component="pre" sx={preSx}>{renderDiffText(node.data?.content || '')}</Typography>
  );
}

function NodeBody({ status, oldNode, newNode, viewMode }) {
  if (status === 'added' || status === 'removed') return renderNodeBody(newNode || oldNode);

  const oldSlot = isSlotNode(oldNode);
  const newSlot = isSlotNode(newNode);
  if (oldSlot !== newSlot) {
    return (
      <Box>
        <Typography sx={{ fontSize: 12, mb: 1, fontStyle: 'italic', color: 'text.secondary' }}>
          Node type changed shape (slot ↔ plain prompt) — showing both versions.
        </Typography>
        {renderNodeBody(oldNode)}
        {renderNodeBody(newNode)}
      </Box>
    );
  }
  if (newSlot) return <SlotVariantDiff oldData={oldNode.data} newData={newNode.data} viewMode={viewMode} />;
  const oldContent = oldNode.data?.content || '';
  const newContent = newNode.data?.content || '';
  return oldContent !== newContent ? <NodeContentDiff oldText={oldContent} newText={newContent} viewMode={viewMode} /> : null;
}

const NodeRow = forwardRef(function NodeRow({ id, oldNode, newNode, status, viewMode, expanded, onToggle }, ref) {
  const node = newNode || oldNode;
  const label = node.data?.label || node.data?.name || id;
  const metaChanges = status === 'changed' ? nodeMetaChanges(oldNode, newNode) : [];
  const hasBody = status !== 'unchanged';

  return (
    <Box ref={ref} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5 }}>
      <Box
        onClick={hasBody ? onToggle : undefined}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, cursor: hasBody ? 'pointer' : 'default' }}
      >
        {hasBody && (
          <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .1s' }} />
        )}
        <Typography sx={{ fontSize: 15, fontWeight: 600, textDecoration: 'underline' }}>{label}</Typography>
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', bgcolor: 'action.hover', borderRadius: 1, px: 0.75, py: 0.15 }}>
          {id}
        </Box>
      </Box>
      {hasBody && (
        <Collapse in={expanded}>
          <Box sx={{ pb: 1.5 }}>
            <MetaChangesTable changes={metaChanges} />
            <NodeBody status={status} oldNode={oldNode} newNode={newNode} viewMode={viewMode} />
          </Box>
        </Collapse>
      )}
    </Box>
  );
});

export default NodeRow;
